/**
 * MobileCalendarView.jsx — Touch-optimized calendar for mobile viewports
 *
 * Views: Month (dot indicators) + Day (detail list) + Agenda (upcoming list)
 * Completely separate from the 1090-line desktop CalendarView.
 * Uses the same data hooks.
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Chip, Paper, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import { useJobs } from '../../hooks/useJobs';
import { useCalendarEvents, useCreateCalendarEvent, useDeleteCalendarEvent } from '../../hooks/useCalendarEvents';
import { useLegalDocs } from '../../hooks/useLegal';
import { useProfiles } from '../../hooks/useProfiles';
import { useAuth } from '../../hooks/useAuth';
import { useRoster } from '../../hooks/useRoster';
import { useFocus } from '../../hooks/useFocus';
import { navigateToJob } from '../../lib/crossNav';
import { normalizeSnapshot, deptLeads } from '../../lib/calcEngine';
import { getUserColor } from '../../lib/userColors';
import { STATUS_COLORS, formatStatus, uid } from '../../lib/utils';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CAL_COLORS = { shoot: '#2E8B57', delivery: '#C94A2A', legal: '#7B5EA7', custom: '#8B5CF6' };

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const suffix = h >= 12 ? 'p' : 'a';
  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m ? `${hr}:${String(m).padStart(2, '0')}${suffix}` : `${hr}${suffix}`;
}

function safeParse(val) {
  if (!val) return null;
  if (typeof val !== 'string') return val;
  try { return JSON.parse(val); } catch { return null; }
}

function getShootTime(job) {
  const raw = safeParse(job.calcSnapshot);
  if (!raw) return null;
  const lines = raw.deliverableLines || [];
  const times = lines.map(l => l.shootTime).filter(Boolean).sort();
  return times.length ? times[0] : null;
}

function getCrewNames(job) {
  const raw = safeParse(job.calcSnapshot);
  if (!raw) return [];
  const snap = normalizeSnapshot(raw);
  const names = new Set();
  (snap.originators || []).forEach(o => { if (o.name) names.add(o.name); });
  deptLeads(snap.prod).forEach(l => { if (l?.name) names.add(l.name); });
  (snap.prod?.hourly || []).forEach(h => { if (h.name) names.add(h.name); });
  deptLeads(snap.post).forEach(l => { if (l?.name) names.add(l.name); });
  (snap.post?.hourly || []).forEach(h => { if (h.name) names.add(h.name); });
  return [...names];
}

const MOBILE_VIEWS = new Set(['month', 'day', 'agenda']);

export default function MobileCalendarView({ calendarScope = 'mine', defaultView = 'month' }) {
  const { data: rawJobs = [] } = useJobs();
  const { data: calEvents = [] } = useCalendarEvents();
  const { data: legalDocs = [] } = useLegalDocs();
  const { data: profiles = [] } = useProfiles();
  const { data: roster = [] } = useRoster();
  const { user } = useAuth();
  const { isHidden } = useFocus();
  const createCalEvent = useCreateCalendarEvent();
  const deleteCalEvent = useDeleteCalendarEvent();
  const navigate = useNavigate();

  // Resolve logged-in user's roster ID
  const myRosterId = useMemo(() => {
    if (!user?.email || !roster.length) return null;
    const match = roster.find(r => r.email === user.email);
    return match?.id || null;
  }, [user?.email, roster]);

  // Filter jobs by scope
  const allJobs = useMemo(() => {
    if (calendarScope !== 'mine' || !myRosterId) return rawJobs;
    return rawJobs.filter(job => {
      const snap = safeParse(job.calcSnapshot);
      if (!snap) return false;
      const ids = new Set();
      (snap.originators || []).forEach(o => { if (o.rosterId || o.roster_id) ids.add(o.rosterId || o.roster_id); });
      deptLeads(snap.prod).forEach(pl => { if (pl?.rosterId || pl?.roster_id) ids.add(pl.rosterId || pl.roster_id); });
      (snap.prod?.hourly || []).forEach(h => { if (h.rosterId || h.roster_id) ids.add(h.rosterId || h.roster_id); });
      deptLeads(snap.post).forEach(ptl => { if (ptl?.rosterId || ptl?.roster_id) ids.add(ptl.rosterId || ptl.roster_id); });
      (snap.post?.hourly || []).forEach(h => { if (h.rosterId || h.roster_id) ids.add(h.rosterId || h.roster_id); });
      return ids.has(myRosterId);
    });
  }, [rawJobs, calendarScope, myRosterId]);

  const safeView = MOBILE_VIEWS.has(defaultView) ? defaultView : 'month';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(safeView); // 'month' | 'day' | 'agenda'
  const [selectedDay, setSelectedDay] = useState(toDateStr(new Date()));
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState({ title: '', date: '', time: '', notes: '' });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const todayStr = toDateStr(new Date());

  // ── Data maps ─────────────────────────────────────────────────────────
  const jobsByDate = useMemo(() => {
    const map = {};
    allJobs.forEach((job) => {
      const dates = new Set();
      if (job.shootDate) dates.add(job.shootDate.split('T')[0]);
      if (Array.isArray(job.shootDates)) job.shootDates.forEach(d => dates.add(d.split('T')[0]));
      dates.forEach(ds => { if (!map[ds]) map[ds] = []; map[ds].push(job); });
    });
    return map;
  }, [allJobs]);

  const eventsByDate = useMemo(() => {
    const map = {};
    calEvents.forEach((ev) => {
      if (!ev.date) return;
      const ds = ev.date.split('T')[0];
      if (!map[ds]) map[ds] = [];
      map[ds].push(ev);
    });
    return map;
  }, [calEvents]);

  const deliveriesByDate = useMemo(() => {
    const map = {};
    const active = ['intake', 'quoted', 'confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced'];
    allJobs.forEach((job) => {
      if (!active.includes(job.status)) return;
      if (job.calcSnapshot) {
        const raw = typeof job.calcSnapshot === 'string' ? JSON.parse(job.calcSnapshot) : job.calcSnapshot;
        const lines = (raw.deliverableLines || []).filter(l => l.deliveryDate);
        const dsArr = Array.isArray(job.deliveryStatus) ? job.deliveryStatus : [];
        if (lines.length) {
          lines.forEach((line, idx) => {
            const lds = line.deliveryDate.split('T')[0];
            if (!map[lds]) map[lds] = [];
            map[lds].push({ type: 'delivery', title: `${job.title} — ${line.desc || 'Deliverable'}`, jobId: job.id, delivered: dsArr[idx]?.sent === true });
          });
          return;
        }
      }
      if (job.deliveryDate) {
        const ds = job.deliveryDate.split('T')[0];
        if (!map[ds]) map[ds] = [];
        map[ds].push({ type: 'delivery', title: job.title || 'Delivery', jobId: job.id, delivered: false });
      }
    });
    return map;
  }, [allJobs]);

  const legalsByDate = useMemo(() => {
    const map = {};
    legalDocs.forEach((doc) => {
      if (!doc.date || doc.status === 'void') return;
      const ds = doc.date.split('T')[0];
      if (!map[ds]) map[ds] = [];
      map[ds].push({ type: 'legal', title: `${doc.type || 'Legal'} — ${doc.party || ''}` });
    });
    return map;
  }, [legalDocs]);

  // Profile name → color map for crew display
  const crewColorMap = useMemo(() => {
    const m = new Map();
    for (const p of profiles) m.set(p.fullName, p.accentColor);
    return m;
  }, [profiles]);

  // Get all event types for a date (for dots)
  const getDateTypes = useCallback((ds) => {
    const types = new Set();
    if (jobsByDate[ds]?.length) types.add('shoot');
    if (deliveriesByDate[ds]?.length) types.add('delivery');
    if (legalsByDate[ds]?.length) types.add('legal');
    if (eventsByDate[ds]?.length) types.add('custom');
    return types;
  }, [jobsByDate, deliveriesByDate, legalsByDate, eventsByDate]);

  // ── Navigation ────────────────────────────────────────────────────────
  const handlePrev = () => {
    if (viewMode === 'month') setCurrentDate(new Date(year, month - 1, 1));
    else if (viewMode === 'day') {
      const d = new Date(selectedDay + 'T12:00:00');
      d.setDate(d.getDate() - 1);
      const ds = toDateStr(d);
      setSelectedDay(ds);
      setCurrentDate(d);
    }
  };
  const handleNext = () => {
    if (viewMode === 'month') setCurrentDate(new Date(year, month + 1, 1));
    else if (viewMode === 'day') {
      const d = new Date(selectedDay + 'T12:00:00');
      d.setDate(d.getDate() + 1);
      const ds = toDateStr(d);
      setSelectedDay(ds);
      setCurrentDate(d);
    }
  };
  const handleToday = () => { setCurrentDate(new Date()); setSelectedDay(todayStr); };

  const handleDayTap = (ds) => {
    setSelectedDay(ds);
    setViewMode('day');
    const d = new Date(ds + 'T12:00:00');
    setCurrentDate(d);
  };

  // ── Event creation ────────────────────────────────────────────────────
  const openNewEvent = () => {
    setEventForm({ title: '', date: selectedDay || todayStr, time: '', notes: '' });
    setShowEventModal(true);
  };

  const handleSaveEvent = () => {
    if (!eventForm.title.trim()) return;
    createCalEvent.mutate({ id: uid(), title: eventForm.title, date: eventForm.date, time: eventForm.time, notes: eventForm.notes });
    setShowEventModal(false);
  };

  // ── Month view ────────────────────────────────────────────────────────
  const monthGrid = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    // Previous month padding
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthDays - i);
      cells.push({ date: d, dateStr: toDateStr(d), inMonth: false });
    }
    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i);
      cells.push({ date: d, dateStr: toDateStr(d), inMonth: true });
    }
    // Next month padding to fill 6 rows
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      cells.push({ date: d, dateStr: toDateStr(d), inMonth: false });
    }
    return cells;
  }, [year, month]);

  const monthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // ── Agenda data ───────────────────────────────────────────────────────
  const agendaItems = useMemo(() => {
    const items = [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    for (let i = 0; i < 30; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const ds = toDateStr(d);
      const dayJobs = jobsByDate[ds] || [];
      const dayDeliveries = deliveriesByDate[ds] || [];
      const dayLegals = legalsByDate[ds] || [];
      const dayEvents = eventsByDate[ds] || [];
      if (dayJobs.length || dayDeliveries.length || dayLegals.length || dayEvents.length) {
        items.push({ date: d, dateStr: ds, jobs: dayJobs, deliveries: dayDeliveries, legals: dayLegals, events: dayEvents });
      }
    }
    return items;
  }, [jobsByDate, deliveriesByDate, legalsByDate, eventsByDate]);

  // ── Day detail data ───────────────────────────────────────────────────
  const dayJobs = selectedDay ? (jobsByDate[selectedDay] || []) : [];
  const dayDeliveries = selectedDay ? (deliveriesByDate[selectedDay] || []) : [];
  const dayLegals = selectedDay ? (legalsByDate[selectedDay] || []) : [];
  const dayEvents = selectedDay ? (eventsByDate[selectedDay] || []) : [];

  const selectedDayLabel = selectedDay
    ? new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : '';

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {viewMode !== 'agenda' && <IconButton size="small" onClick={handlePrev}><ChevronLeftIcon /></IconButton>}
          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem', minWidth: 0, textAlign: 'center' }} onClick={handleToday}>
            {viewMode === 'day' ? selectedDayLabel : viewMode === 'agenda' ? 'Next 30 days' : monthLabel}
          </Typography>
          {viewMode !== 'agenda' && <IconButton size="small" onClick={handleNext}><ChevronRightIcon /></IconButton>}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {['month', 'day', 'agenda'].map(m => (
            <Chip
              key={m}
              label={m.charAt(0).toUpperCase() + m.slice(1)}
              size="small"
              onClick={() => setViewMode(m)}
              sx={{
                fontSize: '0.65rem',
                height: 32,
                position: 'relative',
                bgcolor: viewMode === m ? 'primary.main' : 'transparent',
                color: viewMode === m ? 'white' : 'text.secondary',
                fontWeight: viewMode === m ? 600 : 400,
                '&:hover': { bgcolor: viewMode === m ? 'primary.dark' : 'action.hover' },
                '&::after': { content: '""', position: 'absolute', inset: -6 },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Month View */}
      {viewMode === 'month' && (
        <Box>
          {/* Day headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
            {DAYS.map(d => (
              <Typography key={d} sx={{ textAlign: 'center', fontSize: '0.6rem', color: 'text.disabled', fontWeight: 500 }}>{d}</Typography>
            ))}
          </Box>
          {/* Day cells */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', bgcolor: 'divider', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {monthGrid.map((cell) => {
              const types = getDateTypes(cell.dateStr);
              const isToday = cell.dateStr === todayStr;
              const isSelected = cell.dateStr === selectedDay;
              return (
                <Box
                  key={cell.dateStr}
                  onClick={() => handleDayTap(cell.dateStr)}
                  sx={{
                    bgcolor: 'background.paper',
                    minHeight: 44,
                    p: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    opacity: cell.inMonth ? 1 : 0.3,
                    '&:active': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: isToday ? 700 : 400,
                      width: 24,
                      height: 24,
                      lineHeight: '24px',
                      textAlign: 'center',
                      borderRadius: '50%',
                      bgcolor: isToday ? 'primary.main' : isSelected ? 'action.selected' : 'transparent',
                      color: isToday ? 'white' : 'text.primary',
                    }}
                  >
                    {cell.date.getDate()}
                  </Typography>
                  {/* Event dots */}
                  {types.size > 0 && (
                    <Box sx={{ display: 'flex', gap: '3px', mt: '2px' }}>
                      {[...types].map(t => (
                        <Box key={t} sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: CAL_COLORS[t] }} />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Selected day summary (below calendar) */}
          {selectedDay && (dayJobs.length > 0 || dayDeliveries.length > 0 || dayLegals.length > 0 || dayEvents.length > 0) && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.8rem' }}>{selectedDayLabel}</Typography>
              {renderDayItems()}
            </Box>
          )}
        </Box>
      )}

      {/* Day View */}
      {viewMode === 'day' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            {isHidden('calendar.addEvent') ? <span /> : <Button data-focus="calendar.addEvent" size="small" onClick={openNewEvent} startIcon={<AddIcon />} sx={{ fontSize: '0.7rem' }}>Event</Button>}
            <Button
              size="small"
              onClick={() => navigate(`/jobs?action=newJob&shootDate=${selectedDay}`)}
              sx={{ fontSize: '0.7rem' }}
            >
              + Job
            </Button>
          </Box>
          {dayJobs.length === 0 && dayDeliveries.length === 0 && dayLegals.length === 0 && dayEvents.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 4 }}>No events</Typography>
          ) : (
            renderDayItems()
          )}
        </Box>
      )}

      {/* Agenda View */}
      {viewMode === 'agenda' && (
        <Box>
          {agendaItems.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 4 }}>No upcoming events</Typography>
          ) : (
            agendaItems.map((day) => (
              <Box key={day.dateStr} sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: day.dateStr === todayStr ? 'primary.main' : 'text.secondary',
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {day.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  {day.dateStr === todayStr && ' — Today'}
                </Typography>
                {day.jobs.map((job) => renderJobCard(job))}
                {day.deliveries.map((del, i) => renderDeliveryCard(del, i))}
                {day.legals.map((leg, i) => renderLegalCard(leg, i))}
                {day.events.map((ev) => renderEventCard(ev))}
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Event creation modal */}
      <Dialog open={showEventModal} onClose={() => setShowEventModal(false)} fullScreen>
        <DialogTitle sx={{ fontSize: '1rem' }}>New Event</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Title" size="small" fullWidth value={eventForm.title} onChange={(e) => setEventForm(f => ({ ...f, title: e.target.value }))} autoFocus />
          <TextField label="Date" type="date" size="small" fullWidth value={eventForm.date} onChange={(e) => setEventForm(f => ({ ...f, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField label="Time" type="time" size="small" fullWidth value={eventForm.time} onChange={(e) => setEventForm(f => ({ ...f, time: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField label="Notes" size="small" fullWidth multiline rows={3} value={eventForm.notes} onChange={(e) => setEventForm(f => ({ ...f, notes: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ pb: 'calc(env(safe-area-inset-bottom) + 8px)' }}>
          <Button onClick={() => setShowEventModal(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEvent}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ── Shared day items renderer ─────────────────────────────────────────
  function renderDayItems() {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {dayJobs.map((job) => renderJobCard(job))}
        {dayDeliveries.map((del, i) => renderDeliveryCard(del, i))}
        {dayLegals.map((leg, i) => renderLegalCard(leg, i))}
        {dayEvents.map((ev) => renderEventCard(ev))}
      </Box>
    );
  }

  function renderJobCard(job) {
    const time = getShootTime(job);
    const crew = getCrewNames(job);
    const statusColor = STATUS_COLORS[job.status] || '#666';
    return (
      <Paper
        key={job.id}
        onClick={() => navigateToJob(navigate, job.id)}
        sx={{
          p: 1.5,
          borderLeft: `3px solid ${statusColor}`,
          cursor: 'pointer',
          '&:active': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, flex: 1, mr: 1 }}>{job.title || 'Untitled'}</Typography>
          {time && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontFamily: '"DM Mono", monospace' }}>{fmtTime(time)}</Typography>}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={formatStatus(job.status)} size="small" sx={{ height: 18, fontSize: '0.55rem', bgcolor: statusColor, color: 'white' }} />
          {job.clientName && <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{job.clientName}</Typography>}
        </Box>
        {job.location && (
          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.5 }}>📍 {job.location}</Typography>
        )}
        {crew.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
            {crew.map(name => (
              <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: getUserColor({ name, accentColor: crewColorMap.get(name) }), flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>{name}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    );
  }

  function renderDeliveryCard(del, idx) {
    return (
      <Paper
        key={`del-${idx}`}
        onClick={() => del.jobId && navigateToJob(navigate, del.jobId)}
        sx={{
          p: 1.5,
          borderLeft: `3px solid ${CAL_COLORS.delivery}`,
          cursor: del.jobId ? 'pointer' : 'default',
          opacity: del.delivered ? 0.5 : 1,
          '&:active': { bgcolor: 'action.hover' },
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>📦 {del.title}</Typography>
        {del.delivered && <Chip label="Sent" size="small" sx={{ height: 16, fontSize: '0.5rem', mt: 0.5 }} />}
      </Paper>
    );
  }

  function renderLegalCard(leg, idx) {
    return (
      <Paper key={`leg-${idx}`} sx={{ p: 1.5, borderLeft: `3px solid ${CAL_COLORS.legal}` }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{leg.title}</Typography>
      </Paper>
    );
  }

  function renderEventCard(ev) {
    return (
      <Paper
        key={ev.id}
        sx={{
          p: 1.5,
          borderLeft: `3px solid ${CAL_COLORS.custom}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{ev.title}</Typography>
          {ev.time && <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', fontFamily: '"DM Mono", monospace' }}>{fmtTime(ev.time)}</Typography>}
        </Box>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this event?')) deleteCalEvent.mutate(ev.id); }}
          sx={{ color: 'text.disabled', fontSize: '1rem', width: 40, height: 40 }}
        >
          ✕
        </IconButton>
      </Paper>
    );
  }
}
